import { useEffect, useRef, useState, type ReactNode } from 'react'

import {
  fetchFullDays,
  fetchQuote,
  type TransportOptions,
  type TransportQuote,
  type TripChoice,
} from '../../api/transport'
import { Button } from '../../components/Button'
import { PageBand, PageShell } from '../../components/PageShell'
import { EMERGENCY_NUMBER } from '../../lib/emergencyNumber'
import { useBooking } from './booking'
import { Desk } from './Desk'
import { useFocusOnMount } from './focus'
import { ProgressBar } from './ProgressBar'
import { TransportHeader } from './TransportHeader'
import {
  accraToday,
  at,
  availabilityRanges,
  dayLabel,
  dayOf,
  noticeProblem,
  pickupChoices,
  slots,
  suggestedPickup,
  timeOf,
  toIso,
  windowDays,
} from './tripTimes'

/**
 * Screen 3, trip details (M39-T11, spec §C.3.3): where from, which hospital,
 * when, and the journey (or, for Car Hire, the hire). The page decides
 * nothing the server decides: full days come from the availability endpoint,
 * and the fare zone, or why the trip cannot be booked online, from the quote,
 * re-asked whenever the area, hospital, journey or hire length changes.
 */

/** Spec §C.3.2's footer names the zones this way. */
const ZONE_NAME: Record<number, string> = {
  1: 'Tema',
  2: 'Accra',
  3: 'Outer Greater Accra',
}

/** Waits this long after a change before asking for a quote. */
const QUOTE_DEBOUNCE_MS = 250

type QuoteState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; quote: TransportQuote }
  | { kind: 'failed' }

const field =
  'mt-1 w-full rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-[14px] text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent disabled:opacity-50'
const label = 'text-[13px] font-medium text-ink-1'
const legend = 'font-mono text-[10px] uppercase tracking-wide text-ink-3'
const radio =
  'h-4 w-4 shrink-0 accent-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'

export function TripScreen({
  options,
  onBack,
  onStop,
  onContinue,
}: {
  options: TransportOptions
  onBack: () => void
  /** 112 from this screen ends the booking (not counted: only the gate is). */
  onStop: () => void
  onContinue: () => void
}): ReactNode {
  const { booking, update } = useBooking()
  const heading = useFocusOnMount<HTMLHeadingElement>()
  const service = booking.service ?? 'standard_ride'
  const carHire = service === 'car_hire'
  const kept = booking.trip
  // "Book the same trip again" (M40-T11): the earlier trip fills the places
  // whatever service is chosen now, never the day or the time. A journey and
  // return mode carry between services that have one (not Car Hire); a hire
  // length only from Car Hire to Car Hire.
  const carried = kept ? null : booking.carried
  const carriedHire = carried?.service === 'car_hire'
  const shapeCarried =
    carried !== null && carriedHire === carHire ? carried : null

  const [areaId, setAreaId] = useState(
    kept?.pickupAreaId ?? carried?.pickupAreaId ?? '',
  )
  const [address, setAddress] = useState(
    kept?.pickupAddress ?? carried?.pickupAddress ?? '',
  )
  const [landmark, setLandmark] = useState(
    kept?.pickupLandmark ?? carried?.pickupLandmark ?? '',
  )
  const [gps, setGps] = useState<{ lat: number; lng: number } | null>(
    kept?.pickupLat != null && kept.pickupLng != null
      ? { lat: kept.pickupLat, lng: kept.pickupLng }
      : null,
  )
  const [gpsFailed, setGpsFailed] = useState(false)
  const [hospitalId, setHospitalId] = useState(
    kept?.hospitalId ?? carried?.hospitalId ?? '',
  )
  // The appointment (or, for Car Hire, the start) as a day and a time.
  const keptWhen = kept ? new Date(kept.appointmentAt ?? kept.pickupAt) : null
  const [day, setDay] = useState(keptWhen ? dayOf(keptWhen) : '')
  const [time, setTime] = useState(keptWhen ? timeOf(keptWhen) : '')
  // A pickup the person moved with "Change"; null keeps the suggestion.
  const [pickupOverride, setPickupOverride] = useState<string | null>(null)
  const [changing, setChanging] = useState(false)
  const [journey, setJourney] = useState<'one_way' | 'return'>(
    kept?.journey ?? shapeCarried?.journey ?? 'one_way',
  )
  const [returnMode, setReturnMode] = useState(
    kept?.returnMode ?? shapeCarried?.returnMode ?? null,
  )
  const [returnTime, setReturnTime] = useState(
    kept?.returnPickupAt ? timeOf(new Date(kept.returnPickupAt)) : '',
  )
  const [hireLength, setHireLength] = useState(
    kept?.hireLength ?? shapeCarried?.hireLength ?? null,
  )
  const [stops, setStops] = useState(kept?.hireStops ?? '')
  const [fullDays, setFullDays] = useState<ReadonlySet<string>>(new Set())
  // Some of the window's full days could not be fetched: said, with a retry.
  const [daysUnknown, setDaysUnknown] = useState(false)
  const [daysAttempt, setDaysAttempt] = useState(0)
  // The last answer, with the request it answered: an answer to an older
  // request is never shown for the current one.
  const [answer, setAnswer] = useState<{
    request: string
    state: QuoteState
  } | null>(null)
  // "Try again" after a failed quote asks once more for the same trip.
  const [attempt, setAttempt] = useState(0)

  const today = accraToday(new Date())
  const days = windowDays(today, options.max_days_ahead)

  // Full days, the whole window, a piece at a time. A piece that fails
  // leaves its days open, and the page says so and offers to ask again; the
  // checkout re-checks the day under its lock in any case.
  useEffect(() => {
    const ctrl = new AbortController()
    void Promise.allSettled(
      availabilityRanges(today, options.max_days_ahead).map(([from, to]) =>
        fetchFullDays(service, from, to, ctrl.signal),
      ),
    ).then((results) => {
      if (ctrl.signal.aborted) return
      setFullDays(
        new Set(
          results.flatMap((r) => (r.status === 'fulfilled' ? r.value : [])),
        ),
      )
      setDaysUnknown(results.some((r) => r.status === 'rejected'))
    })
    return () => ctrl.abort()
  }, [service, today, options.max_days_ahead, daysAttempt])

  // The quote: the fare zone, or why the trip cannot be booked online.
  const trip =
    areaId && hospitalId && (!carHire || hireLength)
      ? JSON.stringify({
          service,
          pickup_area_id: areaId,
          hospital_id: hospitalId,
          ...(carHire && hireLength
            ? { hire_length: hireLength }
            : { journey }),
        } satisfies TripChoice)
      : null
  const request = trip === null ? null : `${attempt}:${trip}`
  useEffect(() => {
    if (request === null) return
    const body = JSON.parse(
      request.slice(request.indexOf(':') + 1),
    ) as TripChoice
    const ctrl = new AbortController()
    const timer = setTimeout(() => {
      fetchQuote(body, ctrl.signal).then(
        (q) => setAnswer({ request, state: { kind: 'ready', quote: q } }),
        () => {
          if (!ctrl.signal.aborted)
            setAnswer({ request, state: { kind: 'failed' } })
        },
      )
    }, QUOTE_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [request])
  const quote: QuoteState =
    request === null
      ? { kind: 'idle' }
      : answer?.request === request
        ? answer.state
        : { kind: 'loading' }

  const area = options.areas.find((a) => a.id === areaId)
  // Outside Greater Accra (zone 0) is quoted by phone: said as soon as the
  // area is chosen, not after the rest of the form (spec §C.3.3).
  const shownQuote: QuoteState =
    area?.zone === 0
      ? { kind: 'ready', quote: { bookable: false, reason: 'phone_quote' } }
      : quote
  const appointment = !carHire && day && time ? at(day, time) : null
  const suggested =
    appointment && area
      ? suggestedPickup(
          appointment,
          area.travel_minutes,
          options.pickup_early_arrival_minutes,
        )
      : null
  // A kept pickup that is not the suggestion was moved with "Change". It
  // belongs to the kept appointment and area only: change either and the
  // pickup is suggested afresh.
  const keptPickup =
    kept &&
    !carHire &&
    suggested &&
    appointment &&
    kept.pickupAreaId === areaId &&
    kept.appointmentAt === toIso(appointment) &&
    kept.pickupAt !== toIso(suggested)
      ? kept.pickupAt
      : null
  const override = pickupOverride ?? keptPickup
  const pickup = carHire
    ? day && time
      ? at(day, time)
      : null
    : override && suggested
      ? new Date(override)
      : suggested
  const choices =
    suggested && appointment
      ? pickupChoices(
          suggested,
          options.pickup_change_window_minutes,
          appointment,
        )
      : []

  const minNotice =
    options.services.find((s) => s.service === service)?.min_notice_minutes ??
    null
  const notice = pickup ? noticeProblem(pickup, new Date(), minNotice) : null
  const pickupDayFull = pickup !== null && fullDays.has(dayOf(pickup))
  const notBefore =
    !carHire && appointment !== null && pickup !== null && pickup >= appointment
  const returnTimes = time ? slots().filter((t) => t > time) : []
  const returnReady =
    journey === 'one_way' ||
    returnMode === 'call_to_collect' ||
    (returnMode === 'fixed' && returnTime !== '')
  const answered =
    areaId !== '' &&
    address.trim() !== '' &&
    hospitalId !== '' &&
    pickup !== null &&
    notice === null &&
    !notBefore &&
    !pickupDayFull &&
    // Car Hire's length needs no check here: without it there is no quote.
    (carHire || returnReady)
  const bookable = shownQuote.kind === 'ready' && shownQuote.quote.bookable

  // 3. What is still unanswered, said in words: a disabled Continue alone
  // does not tell anyone why.
  const missing = [
    !areaId && 'pickup area',
    !address.trim() && (carHire ? 'first pickup address' : 'pickup address'),
    !hospitalId && 'destination hospital',
    carHire && !hireLength && 'hire length',
    !day && (carHire ? 'date' : 'appointment date'),
    !time && (carHire ? 'start time' : 'appointment time'),
    !carHire && !returnReady && 'return pickup',
  ].filter((m): m is string => typeof m === 'string')
  const canContinue = answered && bookable

  const pickupSelect = useRef<HTMLSelectElement>(null)
  useEffect(() => {
    if (changing) pickupSelect.current?.focus()
  }, [changing])

  const resetPickup = () => {
    setPickupOverride(null)
    setChanging(false)
  }

  const useGps = () => {
    setGpsFailed(false)
    try {
      navigator.geolocation.getCurrentPosition(
        (p) => setGps({ lat: p.coords.latitude, lng: p.coords.longitude }),
        () => setGpsFailed(true),
        { timeout: 10_000 },
      )
    } catch {
      setGpsFailed(true)
    }
  }

  const save = () => {
    if (!pickup) return
    update({
      trip: {
        pickupAreaId: areaId,
        pickupAddress: address.trim(),
        pickupLandmark: landmark.trim() || null,
        pickupLat: gps?.lat ?? null,
        pickupLng: gps?.lng ?? null,
        hospitalId,
        appointmentAt: appointment ? toIso(appointment) : null,
        pickupAt: toIso(pickup),
        journey: carHire ? null : journey,
        returnMode: !carHire && journey === 'return' ? returnMode : null,
        returnPickupAt:
          !carHire && journey === 'return' && returnMode === 'fixed'
            ? toIso(at(day, returnTime))
            : null,
        hireLength: carHire ? hireLength : null,
        hireStops: carHire ? stops.trim() || null : null,
      },
    })
    onContinue()
  }

  return (
    <PageShell measure={640} as="main">
      <TransportHeader onBack={onBack} />
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col gap-4 px-4 py-5"
      >
        <h1
          ref={heading}
          tabIndex={-1}
          className="font-display text-d-xs font-medium tracking-display text-ink-1 focus:outline-none"
        >
          Trip details
        </h1>
        <ProgressBar current={2} />

        <section aria-labelledby="trip-where" className="flex flex-col gap-3">
          <h2 id="trip-where" className={legend}>
            Where from, and to which hospital
          </h2>
          <label className={label}>
            Pickup area
            <select
              className={field}
              required
              value={areaId}
              onChange={(e) => {
                setAreaId(e.target.value)
                resetPickup()
              }}
            >
              <option value="">Choose an area</option>
              {options.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
          <label className={label}>
            {carHire ? 'First pickup address' : 'Pickup address'}
            <input
              className={field}
              required
              value={address}
              maxLength={200}
              autoComplete="street-address"
              onChange={(e) => setAddress(e.target.value)}
            />
          </label>
          <div>
            <button
              type="button"
              onClick={useGps}
              className="rounded-r3 border border-line-2 px-3 py-1.5 text-[12.5px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              Use GPS
            </button>
            <p aria-live="polite" className="mt-1 text-[12px] text-ink-3">
              {gps
                ? 'Location added for the driver.'
                : gpsFailed
                  ? "We couldn't get your location. The address is enough."
                  : 'Optional: helps the driver find you. Not used for the fare.'}
            </p>
          </div>
          <label className={label}>
            Landmark or directions (optional)
            <input
              className={field}
              value={landmark}
              maxLength={200}
              onChange={(e) => setLandmark(e.target.value)}
            />
          </label>
          <label className={label}>
            Destination hospital
            <select
              className={field}
              required
              value={hospitalId}
              aria-describedby="trip-hospital-help"
              onChange={(e) => setHospitalId(e.target.value)}
            >
              <option value="">Choose a hospital</option>
              {options.hospitals.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </label>
          <p id="trip-hospital-help" className="-mt-2 text-[12px] text-ink-3">
            Network hospitals only. We&apos;ll let the hospital know you&apos;re
            coming.
          </p>
          <QuoteLine
            quote={shownQuote}
            onRetry={() => setAttempt((n) => n + 1)}
          />
        </section>

        {carHire ? (
          <fieldset>
            <legend className={legend}>Hire length</legend>
            <div className="mt-2 flex gap-2">
              {(
                [
                  ['half', 'Half day'],
                  ['full', 'Full day'],
                ] as const
              ).map(([value, text]) => (
                <label
                  key={value}
                  className="flex flex-1 items-center gap-2.5 rounded-r3 border border-line-2 px-3 py-2.5 text-[13px] font-medium text-ink-1"
                >
                  <input
                    type="radio"
                    name="hire-length"
                    className={radio}
                    checked={hireLength === value}
                    onChange={() => setHireLength(value)}
                  />
                  {text}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}

        <section aria-labelledby="trip-when" className="flex flex-col gap-3">
          <h2 id="trip-when" className={legend}>
            When
          </h2>
          <label className={label}>
            {carHire ? 'Date' : 'Appointment date'}
            <select
              className={field}
              required
              value={day}
              aria-describedby={
                fullDays.size > 0 ? 'trip-full-help' : undefined
              }
              onChange={(e) => {
                setDay(e.target.value)
                resetPickup()
              }}
            >
              <option value="">Choose a day</option>
              {days.map((d) => (
                <option key={d} value={d} disabled={fullDays.has(d)}>
                  {fullDays.has(d)
                    ? `${dayLabel(d)} — fully booked`
                    : dayLabel(d)}
                </option>
              ))}
            </select>
          </label>
          {fullDays.size > 0 && (
            <p id="trip-full-help" className="-mt-2 text-[12px] text-ink-3">
              Days marked “fully booked” can&apos;t be chosen.
            </p>
          )}
          {daysUnknown && (
            <p role="status" className="-mt-2 text-[12px] text-ink-2">
              We couldn&apos;t check which days are fully booked. We check the
              day again when you pay.{' '}
              <button
                type="button"
                onClick={() => setDaysAttempt((n) => n + 1)}
                className="font-medium text-accent underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                Try again
              </button>
            </p>
          )}
          <label className={label}>
            {carHire ? 'Start time' : 'Appointment time'}
            <select
              className={field}
              required
              value={time}
              onChange={(e) => {
                setTime(e.target.value)
                resetPickup()
                setReturnTime('')
              }}
            >
              <option value="">Choose a time</option>
              {slots().map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>

          {!carHire && suggested && pickup && (
            <div className="rounded-r3 border border-line-1 bg-bg-soft px-3 py-2.5">
              <p className="text-[13px] text-ink-1">
                Suggested pickup {timeOf(suggested)} — arrives about{' '}
                {options.pickup_early_arrival_minutes} min before the
                appointment.
              </p>
              {override && (
                <p className="mt-1 text-[13px] font-medium text-ink-1">
                  Your pickup: {timeOf(pickup)}
                </p>
              )}
              {changing ? (
                <label className={`${label} mt-2 block`}>
                  Pickup time
                  <select
                    ref={pickupSelect}
                    className={field}
                    value={timeOf(pickup)}
                    onChange={(e) => {
                      const chosen = choices.find(
                        (c) => timeOf(c) === e.target.value,
                      )
                      if (chosen) setPickupOverride(toIso(chosen))
                    }}
                  >
                    {choices.map((c) => (
                      <option key={toIso(c)} value={timeOf(c)}>
                        {timeOf(c)}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <button
                  type="button"
                  onClick={() => setChanging(true)}
                  className="mt-1 text-[12.5px] font-medium text-accent underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Change<span className="sr-only"> the pickup time</span>
                </button>
              )}
            </div>
          )}
          {notice && (
            <p role="alert" className="text-[12.5px] text-sos">
              {notice}
            </p>
          )}
          {!notice && notBefore && (
            <p role="alert" className="text-[12.5px] text-sos">
              The pickup must be before the appointment time.
            </p>
          )}
          {!notice && !notBefore && pickupDayFull && (
            <p role="alert" className="text-[12.5px] text-sos">
              Fully booked — choose another day.
            </p>
          )}
        </section>

        {carHire ? (
          <label className={label}>
            Stops (optional)
            <textarea
              className={field}
              rows={2}
              maxLength={300}
              value={stops}
              onChange={(e) => setStops(e.target.value)}
            />
          </label>
        ) : (
          <>
            <fieldset>
              <legend className={legend}>Journey</legend>
              <div className="mt-2 flex gap-2">
                {(
                  [
                    ['one_way', 'One way'],
                    ['return', 'Return'],
                  ] as const
                ).map(([value, text]) => (
                  <label
                    key={value}
                    className="flex flex-1 items-center gap-2.5 rounded-r3 border border-line-2 px-3 py-2.5 text-[13px] font-medium text-ink-1"
                  >
                    <input
                      type="radio"
                      name="journey"
                      className={radio}
                      checked={journey === value}
                      onChange={() => setJourney(value)}
                    />
                    {text}
                  </label>
                ))}
              </div>
            </fieldset>
            {journey === 'return' && (
              <fieldset>
                <legend className={legend}>Return pickup</legend>
                <div className="mt-2 flex flex-col gap-2">
                  <label className="flex items-center gap-2.5 text-[13px] text-ink-1">
                    <input
                      type="radio"
                      name="return-mode"
                      className={radio}
                      checked={returnMode === 'fixed'}
                      onChange={() => setReturnMode('fixed')}
                    />
                    At a set time
                  </label>
                  {returnMode === 'fixed' && (
                    <label className={`${label} pl-[26px]`}>
                      Return time
                      <select
                        className={field}
                        value={returnTime}
                        onChange={(e) => setReturnTime(e.target.value)}
                      >
                        <option value="">Choose a time</option>
                        {returnTimes.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label className="flex items-center gap-2.5 text-[13px] text-ink-1">
                    <input
                      type="radio"
                      name="return-mode"
                      className={radio}
                      checked={returnMode === 'call_to_collect'}
                      onChange={() => setReturnMode('call_to_collect')}
                    />
                    When discharged — call to collect
                  </label>
                </div>
              </fieldset>
            )}
            <p className="text-[12px] leading-relaxed text-ink-3">
              Booking a course of treatment (e.g. dialysis)? Call the transport
              desk
              <Desk />.
            </p>
          </>
        )}

        <p
          id="trip-missing"
          className="min-h-[1.25rem] text-[12.5px] text-ink-2"
        >
          {missing.length > 0 && `Still needed: ${missing.join(', ')}.`}
        </p>
        <Button
          kind="primary"
          full
          disabled={!canContinue}
          aria-describedby="trip-missing"
          onClick={save}
        >
          Continue
        </Button>

        <p className="rounded-r3 border-l-[3px] border-sos bg-sos-bg px-3 py-2.5 text-[12px] leading-relaxed text-ink-1">
          Medical emergency? Call{' '}
          <a
            href={`tel:${EMERGENCY_NUMBER}`}
            onClick={onStop}
            onAuxClick={onStop}
            className="font-semibold text-sos underline underline-offset-2"
          >
            {EMERGENCY_NUMBER}
          </a>{' '}
          — do not wait for a booking.
        </p>
      </PageBand>
    </PageShell>
  )
}

/** The fare zone, or why the trip cannot be booked online, from the quote. */
function QuoteLine({
  quote,
  onRetry,
}: {
  quote: QuoteState
  onRetry: () => void
}): ReactNode {
  let text: ReactNode = null
  if (quote.kind === 'failed') {
    text = (
      <>
        We couldn&apos;t check the fare.{' '}
        <button
          type="button"
          onClick={onRetry}
          className="font-medium text-accent underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Try again
        </button>
      </>
    )
  } else if (quote.kind === 'ready' && quote.quote.bookable) {
    const zone = quote.quote.zone ?? 0
    text = `Fare zone: Z${zone} · ${ZONE_NAME[zone] ?? ''}`.trim()
  } else if (quote.kind === 'ready') {
    text = refusal(quote.quote.reason)
  }
  return (
    <p aria-live="polite" className="min-h-[1.25rem] text-[12.5px] text-ink-2">
      {text}
    </p>
  )
}

function refusal(reason: string | undefined): ReactNode {
  switch (reason) {
    case 'phone_quote':
      return (
        <>
          We quote these trips by phone. Call the transport desk
          <Desk />.
        </>
      )
    case 'hospital_zone_missing':
      return (
        <>
          We can&apos;t price trips to this hospital online yet. Call the
          transport desk
          <Desk />.
        </>
      )
    case 'unknown_area':
      return 'Choose a pickup area from the list.'
    case 'unknown_hospital':
      return 'Choose a hospital from the list.'
    case 'tariff_missing':
      return (
        <>
          This service can&apos;t be booked online yet. Call the transport desk
          <Desk />.
        </>
      )
    default:
      return (
        <>
          This trip can&apos;t be booked online. Call the transport desk
          <Desk />.
        </>
      )
  }
}
